import React, { useState, useEffect, useCallback } from 'react';
import { Text } from '@cuemath/web-comps';
import { useGetUsers, usePostAgent, invalidateAgents } from 'actions/support-agent';
import {
  AGENT_ROLES,
  ROLE_LABELS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
  TEAM_PICKER_HELPER,
  TEAM_PICKER_REQUIRED_ERROR,
} from '../../constants';
import {
  AgentSearchResults,
  AgentSearchItem,
  AgentSearchInfo,
  AgentSearchName,
  AgentSearchEmail,
  AgentSearchId,
  FormField,
  FormLabel,
  SearchInputWrap,
  SearchTextInput,
  TeamPickerHelper,
} from './styled';
import TeamPicker from './team-picker';
import RolePicker from './role-picker';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnPrimary,
  BtnSecondary,
  IconBtn,
  ModalErrorMsg,
  SA_COLORS,
} from '../../styled-comps';
import Icon from '../icon';

const AddAgentModal = ({ open, onClose, existingAgentIds }) => {
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [role, setRole] = useState(AGENT_ROLES.AGENT);
  const [teamIds, setTeamIds] = useState([]);
  const [teamError, setTeamError] = useState(null);
  const [addError, setAddError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const {
    data: searchData,
    get: searchUsers,
    isProcessing: searching,
    isProcessingFailed: searchFailed,
  } = useGetUsers();

  const { post: postAgent, isProcessing: adding } = usePostAgent({
    onComplete: error => {
      if (error) {
        setAddError(typeof error === 'string' ? error : 'Failed to add agent');
      } else {
        invalidateAgents(CACHE_KEYS.AGENTS);
        handleClose();
      }
    },
  });

  const handleClose = useCallback(() => {
    setQuery('');
    setSelectedUser(null);
    setRole(AGENT_ROLES.AGENT);
    setTeamIds([]);
    setTeamError(null);
    setAddError(null);
    setHasSearched(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!addError) return;
    const timer = setTimeout(() => setAddError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [addError]);

  const handleSearch = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSelectedUser(null);
    setHasSearched(true);
    searchUsers(CACHE_KEYS.AGENT_SEARCH, { query: trimmed, type: 'staff' });
  }, [query, searchUsers]);

  const handleSubmit = useCallback(() => {
    if (!selectedUser) return;
    if (teamIds.length === 0) {
      setTeamError(TEAM_PICKER_REQUIRED_ERROR);
      return;
    }
    setTeamError(null);
    postAgent({
      user_id: selectedUser.id,
      name: selectedUser.name,
      email: selectedUser.email || '',
      role,
      team_ids: teamIds,
    });
  }, [selectedUser, role, teamIds, postAgent]);

  if (!open) return null;

  const users = searchData?.users || [];
  const existingIds = new Set(existingAgentIds || []);
  const filteredUsers = users.filter(u => !existingIds.has(u.id));

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">Add New Agent</Text>
          <IconBtn onClick={handleClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>
        <ModalBody>
          {addError && <ModalErrorMsg>{addError}</ModalErrorMsg>}

          <FormField>
            <FormLabel>Search User</FormLabel>
            <SearchInputWrap>
              <Icon name="search" size={14} />
              <SearchTextInput
                placeholder="Search by name, email, or user ID..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <BtnPrimary onClick={handleSearch} disabled={!query.trim() || searching}>
                {searching ? 'Searching...' : 'Search'}
              </BtnPrimary>
            </SearchInputWrap>
          </FormField>

          {hasSearched && (
            <AgentSearchResults>
              {searching && (
                <AgentSearchItem disabled>
                  <AgentSearchName>Searching...</AgentSearchName>
                </AgentSearchItem>
              )}
              {!searching && searchFailed && (
                <AgentSearchItem disabled>
                  <ModalErrorMsg>Failed to search users</ModalErrorMsg>
                </AgentSearchItem>
              )}
              {!searching && !searchFailed && filteredUsers.length === 0 && (
                <AgentSearchItem disabled>
                  <AgentSearchName>
                    {users.length > 0 ? 'Agent already added' : 'No users found'}
                  </AgentSearchName>
                </AgentSearchItem>
              )}
              {!searching &&
                !searchFailed &&
                filteredUsers.map(user => (
                  <AgentSearchItem
                    key={user.id}
                    $selected={selectedUser?.id === user.id}
                    onClick={() => setSelectedUser(user)}
                  >
                    <AgentSearchInfo>
                      <AgentSearchName>{user.name}</AgentSearchName>
                      {user.email && <AgentSearchEmail>{user.email}</AgentSearchEmail>}
                      <AgentSearchId>{user.id}</AgentSearchId>
                    </AgentSearchInfo>
                    {selectedUser?.id === user.id && (
                      <Icon name="check" size={16} color={SA_COLORS.SUCCESS_600} />
                    )}
                  </AgentSearchItem>
                ))}
            </AgentSearchResults>
          )}

          {selectedUser && (
            <>
              <FormField>
                <FormLabel>Assign Role</FormLabel>
                <RolePicker
                  value={role}
                  onChange={setRole}
                  options={[
                    { value: AGENT_ROLES.AGENT, label: ROLE_LABELS.agent },
                    { value: AGENT_ROLES.LEAD, label: ROLE_LABELS.lead },
                  ]}
                />
              </FormField>

              <FormField>
                <FormLabel>Assign Teams *</FormLabel>
                <TeamPicker
                  value={teamIds}
                  onChange={next => {
                    setTeamIds(next);
                    if (next.length > 0) setTeamError(null);
                  }}
                  error={teamError}
                />
                <TeamPickerHelper>{TEAM_PICKER_HELPER}</TeamPickerHelper>
              </FormField>
            </>
          )}
        </ModalBody>
        <ModalFoot>
          <BtnSecondary onClick={handleClose}>Cancel</BtnSecondary>
          <BtnPrimary
            onClick={handleSubmit}
            disabled={!selectedUser || teamIds.length === 0 || adding}
          >
            {adding ? 'Adding...' : 'Add Agent'}
          </BtnPrimary>
        </ModalFoot>
      </ModalCard>
    </ModalOverlay>
  );
};

export default AddAgentModal;
